/**
 * Shared user / actor avatar primitive.
 *
 * Two modes — chosen automatically:
 *
 *   - Image: `url` is set, the rendered <img> uses Clerk's profile
 *     image.  Falls back to initials if the URL fails to load.
 *   - Initials: derived from `name` (or the local-part of `email`).
 *     Background colour is a deterministic hash of `identity` so the
 *     same person renders in the same hue across the dashboard.
 *
 * Keep this primitive small.  Surfaces that need extra adornment
 * (status dots, hover popovers) compose around it instead of adding
 * variants here.
 */
export function Avatar({
  url = null,
  name = '',
  email = '',
  identity = '',
  size = 24,
  accent = false,
}) {
  const initials = computeInitials(name, email);
  const hueKey = identity || email || name;

  if (url) {
    return (
      <img
        src={url}
        alt=""
        className="spot-avatar"
        style={{ width: size, height: size, fontSize: Math.max(10, Math.round(size * 0.42)) }}
        referrerPolicy="no-referrer"
      />
    );
  }

  let h = 0;
  for (let i = 0; i < hueKey.length; i++) h = (h * 31 + hueKey.charCodeAt(i)) | 0;
  const hue = Math.abs(h) % 360;
  const background = accent
    ? 'var(--brand-600, #4f46e5)'
    : `hsl(${hue}, 36%, 52%)`;

  return (
    <span
      className="spot-avatar spot-avatar--initials"
      style={{
        width: size,
        height: size,
        background,
        fontSize: Math.max(10, Math.round(size * 0.42)),
      }}
      aria-hidden="true"
    >
      {initials}
    </span>
  );
}

function computeInitials(name, email) {
  const display = (name || '').trim();
  if (display) {
    const parts = display.split(/\s+/).slice(0, 2);
    const out = parts.map((p) => p[0]?.toUpperCase() || '').join('');
    if (out) return out;
  }
  const local = (email || '').split('@')[0] || '';
  const cleaned = local.replace(/[^a-zA-Z]/g, '').slice(0, 2).toUpperCase();
  return cleaned || '?';
}
